import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  CreditCard, 
  Smartphone,
  Building2,
  Check,
  Shield,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios from "axios";
import OrderPausedModal from "../components/OrderPausedModal";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();

const PAYMENT_METHODS = [
  { id: "upi", label: "UPI", icon: Smartphone, desc: "Google Pay, PhonePe, Paytm" },
  { id: "card", label: "Credit/Debit Card", icon: CreditCard, desc: "Visa, Mastercard, RuPay" },
  { id: "netbanking", label: "Net Banking", icon: Building2, desc: "All major banks" }
];

const FleetyGoPaymentPage = () => {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState("upi");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    upi_id: "",
    card_number: "",
    card_expiry: "",
    card_cvv: "",
    card_name: ""
  });
  const [error, setError] = useState("");
  const [showOrderPausedModal, setShowOrderPausedModal] = useState(false);
  const [orderPausedData, setOrderPausedData] = useState({ message: null, resumeAt: null });

  useEffect(() => {
    const bookingData = sessionStorage.getItem("fleetygo_booking");
    if (!bookingData) {
      navigate("/fleetygo/book");
      return;
    }
    setBooking(JSON.parse(bookingData));
  }, [navigate]);

  const validatePayment = () => {
    setError("");
    
    if (selectedMethod === "upi") {
      if (!paymentDetails.upi_id.includes("@")) {
        setError("Please enter a valid UPI ID (e.g., name@upi)");
        return false;
      }
    }
    
    if (selectedMethod === "card") {
      if (paymentDetails.card_number.replace(/\s/g, "").length !== 16) {
        setError("Please enter a valid 16-digit card number");
        return false;
      }
      if (!paymentDetails.card_expiry.match(/^\d{2}\/\d{2}$/)) {
        setError("Please enter expiry in MM/YY format");
        return false;
      }
      if (paymentDetails.card_cvv.length !== 3) {
        setError("Please enter a valid 3-digit CVV");
        return false;
      }
      if (!paymentDetails.card_name.trim()) {
        setError("Please enter cardholder name");
        return false;
      }
    }
    
    return true;
  };

  const handlePayment = async () => {
    if (!validatePayment()) return;
    
    setIsProcessing(true);
    setError("");
    
    try {
      // Check if FleetyGo (Package) orders are being accepted
      const statusResponse = await axios.get(`${API_URL}/api/fleetygo-status`);
      if (!statusResponse.data.orders_accepted) {
        setOrderPausedData({
          message: statusResponse.data.message,
          resumeAt: statusResponse.data.resume_at
        });
        setShowOrderPausedModal(true);
        setIsProcessing(false);
        return;
      }

      // Create FleetyGo order first
      const orderResponse = await axios.post(`${API_URL}/api/fleetygo/orders`, {
        user_id: booking.user_id,
        sender_name: booking.pickup.name,
        sender_phone: booking.pickup.phone,
        sender_address: booking.pickup.address,
        sender_address_line2: booking.pickup.address_line2,
        sender_lat: booking.pickup.lat,
        sender_lng: booking.pickup.lng,
        receiver_name: booking.drop.name,
        receiver_phone: booking.drop.phone,
        receiver_address: booking.drop.address,
        receiver_address_line2: booking.drop.address_line2,
        receiver_lat: booking.drop.lat,
        receiver_lng: booking.drop.lng,
        weight_category: booking.packageDetails.weight_category,
        package_contents: booking.packageDetails.contents,
        package_description: booking.packageDetails.description,
        payment_method: selectedMethod
      });

      const orderId = orderResponse.data.order_id;
      const orderNumber = orderResponse.data.order_number;

      // Create Cashfree payment order
      const userData = JSON.parse(localStorage.getItem("fleety_user") || "{}");
      const cashfreeResponse = await axios.post(`${API_URL}/api/payment/create-order`, {
        order_id: `FLEETYGO_${orderNumber}`,
        order_amount: booking.fare.total,
        customer_name: booking.pickup.name,
        customer_email: userData.email || `${booking.pickup.phone}@fleety.co.in`,
        customer_phone: booking.pickup.phone,
        return_url: `${window.location.origin}/fleetygo/payment-status/${orderId}`
      });

      if (cashfreeResponse.data.success) {
        // Store order ID for later reference
        sessionStorage.setItem("fleetygo_pending_order", orderId);
        
        const paymentSessionId = cashfreeResponse.data.payment_session_id;
        
        // Use Cashfree SDK for checkout (recommended for production)
        if (window.Cashfree) {
          const cashfree = window.Cashfree({
            mode: "production"  // Production mode for live payments
          });
          
          cashfree.checkout({
            paymentSessionId: paymentSessionId,
            redirectTarget: "_self"
          }).then((result) => {
            if (result.error) {
              console.error("Payment error:", result.error);
              setError("Payment failed: " + result.error.message);
              setIsProcessing(false);
            }
          }).catch((error) => {
            console.error("Cashfree checkout error:", error);
            setError("Payment failed. Please try again.");
            setIsProcessing(false);
          });
        } else {
          // Fallback: Direct redirect if SDK not loaded
          window.location.href = cashfreeResponse.data.payment_link;
        }
      } else {
        throw new Error("Failed to create payment session");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setError(error.response?.data?.detail || "Payment failed. Please try again.");
      setIsProcessing(false);
    }
  };

  const handleGoBack = () => {
    // Restore booking data to individual fields before going back
    if (booking) {
      sessionStorage.setItem("fleetygo_pickup", JSON.stringify(booking.pickup));
      sessionStorage.setItem("fleetygo_drop", JSON.stringify(booking.drop));
      sessionStorage.setItem("fleetygo_package", JSON.stringify(booking.packageDetails));
      sessionStorage.setItem("fleetygo_step", "4"); // Go back to review step
    }
    navigate("/fleetygo/book");
  };

  if (!booking) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="fleetygo-payment">
      {/* Header */}
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={handleGoBack} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-white text-lg font-semibold">Payment</h1>
      </header>

      <main className="flex-1 p-4 overflow-y-auto">
        {/* Amount Summary */}
        <div className="bg-gradient-to-r from-[#3FA9F5] to-[#2196F3] rounded-xl p-4 mb-4 text-white">
          <p className="text-sm text-white/80">Amount to Pay</p>
          <p className="text-3xl font-bold">₹{booking.fare?.total}</p>
          <p className="text-xs text-white/70 mt-1">
            {booking.fare?.distance_km} km • {booking.pickup.address.split(",")[0]} → {booking.drop.address.split(",")[0]}
          </p>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Select Payment Method</h3>
          <div className="space-y-2">
            {PAYMENT_METHODS.map((method) => (
              <button
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`w-full p-3 rounded-lg border-2 flex items-center gap-3 transition-colors ${
                  selectedMethod === method.id
                    ? "border-[#3FA9F5] bg-[#3FA9F5]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedMethod === method.id ? "bg-[#3FA9F5]" : "bg-gray-100"
                }`}>
                  <method.icon size={20} className={selectedMethod === method.id ? "text-white" : "text-gray-500"} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-sm">{method.label}</p>
                  <p className="text-xs text-gray-500">{method.desc}</p>
                </div>
                {selectedMethod === method.id && (
                  <div className="w-6 h-6 bg-[#3FA9F5] rounded-full flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Details */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Enter Details</h3>
          
          {selectedMethod === "upi" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
              <Input
                value={paymentDetails.upi_id}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, upi_id: e.target.value })}
                placeholder="yourname@upi"
              />
            </div>
          )}

          {selectedMethod === "card" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                <Input
                  value={paymentDetails.card_number}
                  onChange={(e) => setPaymentDetails({ 
                    ...paymentDetails, 
                    card_number: e.target.value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim()
                  })}
                  placeholder="1234 5678 9012 3456"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry</label>
                  <Input
                    value={paymentDetails.card_expiry}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, "").slice(0, 4);
                      if (val.length >= 2) val = val.slice(0, 2) + "/" + val.slice(2);
                      setPaymentDetails({ ...paymentDetails, card_expiry: val });
                    }}
                    placeholder="MM/YY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                  <Input
                    value={paymentDetails.card_cvv}
                    onChange={(e) => setPaymentDetails({ 
                      ...paymentDetails, 
                      card_cvv: e.target.value.replace(/\D/g, "").slice(0, 3)
                    })}
                    placeholder="123"
                    type="password"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
                <Input
                  value={paymentDetails.card_name}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, card_name: e.target.value })}
                  placeholder="Name on card"
                />
              </div>
            </div>
          )}

          {selectedMethod === "netbanking" && (
            <div className="text-center py-4 text-gray-500">
              <p className="text-sm">You will be redirected to your bank's website</p>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
            {error}
          </div>
        )}

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 text-gray-400 text-xs mb-4">
          <Shield size={14} />
          <span>Secured by 256-bit SSL encryption</span>
        </div>
      </main>

      {/* Pay Button */}
      <div className="p-4 bg-white border-t">
        <Button
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full bg-[#3FA9F5] hover:bg-[#3FA9F5]/90 text-white py-3 rounded-xl font-semibold"
        >
          {isProcessing ? (
            <>
              <Loader2 size={20} className="animate-spin mr-2" />
              Processing Payment...
            </>
          ) : (
            <>Pay ₹{booking.fare?.total}</>
          )}
        </Button>
      </div>

      {/* Order Paused Modal */}
      <OrderPausedModal
        isOpen={showOrderPausedModal}
        onClose={() => setShowOrderPausedModal(false)}
        message={orderPausedData.message}
        resumeAt={orderPausedData.resumeAt}
        onBrowseRestaurants={() => {
          setShowOrderPausedModal(false);
          navigate("/fleetygo");
        }}
      />
    </div>
  );
};

export default FleetyGoPaymentPage;
